import { Component, inject, OnInit } from '@angular/core';
import { AppAuthService, LowScoreFallbackRequiredError } from './app-auth.service';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { NotificationService } from '../service/notification.service';
import { FormActionsComponent } from '../form-actions.component';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationService } from '..';
import { RecaptchaV2FallbackComponent } from './recaptcha-v2-fallback.component';

function isAppInternalPath(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\');
}

@Component({
  selector: 'app-reset',
  templateUrl: 'reset.component.html',
  styleUrls: ['login-reset-register.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    RouterModule,
    FormActionsComponent,
    TranslateModule,
    RecaptchaV2FallbackComponent,
  ],
})
export class ResetComponent implements OnInit {
  private readonly translate = inject(TranslationService);
  private readonly router = inject(Router);
  private readonly authService = inject(AppAuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly toastr = inject(NotificationService);

  token!: string;
  email?: string;
  readonly invite = this.route.snapshot.data?.['invite'] === true;
  showV2Fallback = false;
  private pendingRetry: 'verify' | 'complete' | null = null;
  form = new FormBuilder().nonNullable.group({
    password: [
      undefined as string | undefined,
      [
        Validators.minLength(8),
        Validators.required,
        (control: AbstractControl) => {
          const value = control.value;
          if (!value) return null;
          return value.includes(' ') ? { containsSpaces: true } : null;
        },
      ],
    ],
    rePassword: [
      undefined as string | undefined,
      (control: AbstractControl) =>
        control.value === this.form?.controls?.password?.value ? null : { passwordMismatch: true },
    ],
  });

  async updatePassword() {
    try {
      await this.authService.resetPasswordCompete(this.token, this.form.controls.password.value!);
      await this.continueToLogin();
    } catch (err) {
      if (err instanceof LowScoreFallbackRequiredError) {
        this.showV2Fallback = true;
        this.pendingRetry = 'complete';
        return;
      }
      throw err;
    }
  }

  async onV2Solved(token: string) {
    const retry = this.pendingRetry;
    this.pendingRetry = null;
    if (retry === 'verify') {
      try {
        this.email = (await this.authService.verifyToken(this.token, token))?.email;
        this.showV2Fallback = false;
      } catch (e) {
        if (e instanceof LowScoreFallbackRequiredError) {
          // server rejected v2 token too — restore state so user can retry
          this.pendingRetry = 'verify';
          return;
        }
        this.showV2Fallback = false;
        await this.router.navigate(['/reset'], { queryParams: { invalid: true } });
        console.error(e);
      }
    } else {
      try {
        await this.authService.resetPasswordCompete(this.token, this.form.controls.password.value!, token);
        this.showV2Fallback = false;
        await this.continueToLogin();
      } catch (e) {
        if (e instanceof LowScoreFallbackRequiredError) {
          // server rejected v2 token too — restore state so user can retry
          this.pendingRetry = 'complete';
          return;
        }
        this.showV2Fallback = false;
        throw e;
      }
    }
  }

  private async continueToLogin() {
    this.toastr.success('Password_set_please_sign_in_tr');
    await this.router.navigate(['/login'], { queryParams: this.loginQueryParams() });
  }

  private loginQueryParams(): Params {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    return {
      ...(this.email ? { email: this.email } : {}),
      ...(returnUrl && isAppInternalPath(returnUrl) ? { returnUrl } : {}),
    };
  }

  async ngOnInit() {
    this.route.params.subscribe(async ({ token }) => {
      this.token = token;
      try {
        this.email = (await this.authService.verifyToken(token))?.email;
      } catch (e) {
        if (e instanceof LowScoreFallbackRequiredError) {
          this.showV2Fallback = true;
          this.pendingRetry = 'verify';
          return;
        }
        await this.router.navigate(['/reset'], { queryParams: { invalid: true } });
        console.error(e);
      }
    });
    this.form.controls.password.valueChanges.subscribe((value) => {
      if (value?.includes(' ')) {
        this.form.controls.password.setValue(value.replace(/\s/g, ''), { emitEvent: false });
      }
      this.form.patchValue({ rePassword: this.form.value.rePassword || '' }, { emitEvent: false });
    });
    this.form.controls.rePassword.valueChanges.subscribe(() =>
      this.form.patchValue({ password: this.form.value.password || '' }, { emitEvent: false })
    );
  }
}
